import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CandidateAboutDialog } from 'components/CandidateAbout';
import { useFormContext } from 'react-hook-form';
import { schema as parentSchema } from './Step2';
import { getCandidateCommentaryOptions } from 'client/@tanstack/react-query.gen';
import { Avatar } from 'components/Avatar';
import { getAvatarImageProps } from 'helpers/profileImage';
import { FormCommentary } from './FormCommentary';
import { IconTrash2 } from 'components/UI/Icons/Trash2';
import { Button } from 'components/UI/Tailwind/Button';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import {
  CandidateTier,
  type PipelineCandidateSearchFilterSortResponse,
} from 'client/types.gen';
import { Dialog } from 'components/UI/Tailwind/Dialog';
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
} from 'components/UI/Tailwind/Dropdown';
import { IconMoreHorizontal } from 'components/UI/Icons/MoreHorizontal';
import { useNewRecommendationsContext } from './NewRecommendationsContext';
import { IconEdit } from 'components/UI/Icons/Edit';
import { toast } from 'react-hot-toast/headless';

type ParentSchema = z.infer<typeof parentSchema>;

export function RecommendationPanel({
  candidate,
}: {
  candidate: PipelineCandidateSearchFilterSortResponse;
}) {
  const { t } = useTranslation();

  const { data: commentary } = useQuery({
    ...getCandidateCommentaryOptions({
      path: {
        candidate_uuid: candidate.uuid,
      },
    }),
  });

  const { watch, setValue } = useFormContext<ParentSchema>();
  const watchCandidate = watch(`candidates.${candidate.uuid}`);

  // Handles initial load of commentary.
  useEffect(() => {
    // If we don't have the necessary data, or the data is already set, return.
    if (!(commentary && candidate) || watchCandidate) {
      return;
    }

    // Profile highlights start unticked: the AI drafts written for this
    // pipeline are the default.
    const defaultHighlights: Record<string, boolean> = {};

    // If this is a draft recommendation, hydrate the form
    // with its existing draft data.
    if (candidate.recommendation) {
      setValue(
        `candidates.${candidate.uuid}`,
        {
          intro_email_type: candidate.intro_email_type ?? 'manual',
          highlights_with_selected: candidate.recommendation?.highlight_uuids
            ?.length
            ? candidate.recommendation.highlight_uuids.reduce(
                (acc, highlightUuid) => {
                  acc[highlightUuid] = true;

                  return acc;
                },
                {} as Record<string, boolean>,
              )
            : defaultHighlights,
          about_uuid:
            candidate.recommendation.introduction_template_uuid ||
            commentary?.introduction_templates?.[0]?.uuid ||
            '',
          // AI highlights saved on the draft; otherwise drafted anew.
          ...(candidate.recommendation.tailored_highlights?.length
            ? {
                tailored_highlights: (
                  candidate.recommendation.tailored_highlights ?? []
                ).map((h) => ({
                  highlight: h.highlight,
                  is_selected: h.is_selected ?? true,
                  generated: h.generated,
                })),
                tailored_task_id: 'saved',
              }
            : {}),
        },
        { shouldTouch: true },
      );
      return;
    }

    setValue(
      `candidates.${candidate.uuid}`,
      {
        intro_email_type: candidate.intro_email_type ?? 'manual',
        highlights_with_selected: defaultHighlights,
        about_uuid:
          commentary?.introduction_templates?.find(
            (template) => !template.hidden,
          )?.uuid || '',
      },
      { shouldTouch: true },
    );
  }, [commentary, watch, watchCandidate, setValue, candidate]);

  const [isCandidateAboutOpen, setIsCandidateAboutOpen] = useState(false);
  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);

  const { removeCandidate } = useNewRecommendationsContext();

  if (!watchCandidate) {
    return (
      <div className="max-w-[500px] flex-1 overflow-y-auto bg-gray-50">
        <div className="h-full overflow-y-auto rounded-xl bg-white p-5"></div>
      </div>
    );
  }

  return (
    <div className="max-w-[500px] flex-1 overflow-y-auto bg-gray-50">
      <div className="h-full overflow-y-auto rounded-xl bg-white p-5">
        <div className="mb-4 flex items-center gap-x-2 rounded-[8px]">
          <Avatar
            {...getAvatarImageProps(candidate)}
            isClient={candidate.tier === CandidateTier.EXECUTIVE_CLIENT}
            placeholderName={candidate.name ?? undefined}
            shape="rounded"
            size={40}
          />
          <span className="flex flex-1 flex-col gap-x-2">
            <Button
              className="text-14 font-semibold text-gray-800"
              variant="default-link"
              to={`/candidates/$candidateId`}
              params={{ candidateId: candidate.uuid }}
              size={26}
              target="_blank"
            >
              {candidate.name}
            </Button>
            <span className="text-12 font-normal text-gray-600">
              {candidate.current_role}
            </span>
          </span>
          <div className="flex items-center gap-x-4">
            <Dropdown
              button={
                <Button
                  variant="gray-quaternary"
                  as={DropdownButton}
                  iconStart={<IconMoreHorizontal />}
                ></Button>
              }
              className="z-10"
              position="top-right"
            >
              <DropdownItem
                iconStart={<IconEdit />}
                onClick={() => {
                  setIsCandidateAboutOpen(true);
                }}
              >
                {t('form.pipelineRecommendations.editCandidate')}
              </DropdownItem>
              <DropdownItem
                iconStart={<IconTrash2 />}
                onClick={() => {
                  setIsOpenDeleteDialog(true);
                }}
                variant="red"
              >
                {t('form.pipelineRecommendations.removeCandidate')}
              </DropdownItem>
            </Dropdown>
          </div>
        </div>

        <FormCommentary
          candidateUuid={candidate.uuid}
          className="pt-4"
          onEdit={() => setIsCandidateAboutOpen(true)}
          showBulkActions
          showLastModified
        />
        <CandidateAboutDialog
          candidate={candidate}
          isOpen={isCandidateAboutOpen}
          onClose={() => setIsCandidateAboutOpen(false)}
        />
        <Dialog
          heading={t('modal.removeRecommendation.title')}
          actionButtonProps={{
            children: t('modal.removeRecommendation.okText'),
          }}
          open={isOpenDeleteDialog}
          onClose={() => setIsOpenDeleteDialog(false)}
          onSubmit={() => {
            removeCandidate(candidate.uuid);

            setTimeout(() => {
              toast.success(t('modal.removeRecommendation.message'));
            }, 500);

            setIsOpenDeleteDialog(false);
          }}
          body={t('modal.removeRecommendation.text', {
            name: candidate.name,
          })}
        ></Dialog>
      </div>
    </div>
  );
}
